import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import DisplayPost, { PostModel as DisplayPostModel } from '../components/DisplayPost';
import CommentSection from '../components/CommentSection';
import PostSettings from '../components/post-settings';
import type { PostEditorHandle } from '../components/DisplayPost';
import PageNavigation from '../components/PageNavigation';
import SeoTools from '../components/SeoTools';
import { listPosts, savePost, getPost, deletePost, postExists } from '../lib/posts';
import type { PostModel as ApiPostModel } from '../lib/posts';
import { HexColorPicker } from 'react-colorful';
import { listMedia, type MediaItem } from '../lib/media';
import UserMenu from '../components/UserMenu';
import { useUIStore } from '../store/uiStore';
import { type AuthUser, getCachedAuthUser, authStatus, authLogout, setCachedAuthUser, AUTH_CACHE_KEY } from '../lib/auth';

type PageModel = { title?: string; blocks: any[] };

const starter: DisplayPostModel = {
  title: 'New Post',
  excerpt: 'Write a captivating summary for your post…',
  cover: undefined,
  // Comments disabled by default; can be toggled in Post Settings
  commentsEnabled: false as any,
  blocks: [
    { id: Math.random().toString(36).slice(2, 10), type: 'heading1', text: 'Your amazing headline — Page 1' },
    { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Start writing your story here. You can make text bold, italic, add quotes, images and code blocks. Everything updates live.' },
    { id: Math.random().toString(36).slice(2, 10), type: 'quote', text: 'Simplicity is the soul of efficiency.' },
    { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Press the toolbar buttons to change styles or insert blocks. Click anywhere and type freely.' },
  ],
};

const PostEditor: React.FC = () => {
  const chromeHidden = !!useUIStore((s) => (s as any).chromeHidden);
  const setChromeHidden = useUIStore((s) => (s as any).setChromeHidden);
  const [post, setPost] = useState<DisplayPostModel>(starter);
  const postStateRef = useRef<DisplayPostModel>(starter);
  const [posts, setPosts] = useState<{ slug: string; title?: string; size: number; mtime: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSlug, setCurrentSlug] = useState<string | null>(null);
  const postRef = useRef<PostEditorHandle | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => getCachedAuthUser());
  const [color, setColor] = useState<string>('#1f2937');
  const [fontSize, setFontSize] = useState<number>(16);
  const [fontFamily, setFontFamily] = useState<string>('');
  const [hasMarker, setHasMarker] = useState<boolean>(false);
  const [markedLines, setMarkedLines] = useState<number>(0);
  const [selectedLines, setSelectedLines] = useState<number>(0);
  const [applyToSelectionOnly, setApplyToSelectionOnly] = useState<boolean>(false);
  const [autoBullet, setAutoBullet] = useState<boolean>(false);
  const [autoNumber, setAutoNumber] = useState<boolean>(false);
  const [selectionArrowMode, setSelectionArrowMode] = useState<'nothing' | 'move'>('move');
  const lastInlineStyleRef = useRef<boolean>(false);
  // Media picker
  const [mediaOpen, setMediaOpen] = useState<boolean>(false);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaLoading, setMediaLoading] = useState<boolean>(false);
  const [mediaMultiSel, setMediaMultiSel] = useState<string[]>([]);
  // Image filter state for the media picker
  const imageFilterKeys = ['all','svg','png','jpg','jpeg','gif','webp','bmp','tiff','ico','heif','heic'] as const;
  const [mediaFilter, setMediaFilter] = useState<string>('all');
  const imageExt = (it: MediaItem): string => {
    const n = (it.name || '').toLowerCase();
    const m = (it.mime || '').toLowerCase();
    if (n.endsWith('.svg') || m === 'image/svg+xml') return 'svg';
    if (n.endsWith('.jpeg') || m === 'image/jpeg') return 'jpeg';
    if (n.endsWith('.jpg')) return 'jpg';
    if (n.endsWith('.png') || m === 'image/png' || m === 'image/x-png') return 'png';
    if (n.endsWith('.gif') || m === 'image/gif') return 'gif';
    if (n.endsWith('.webp') || m === 'image/webp') return 'webp';
    if (n.endsWith('.bmp') || m === 'image/bmp') return 'bmp';
    if (n.endsWith('.tiff') || m === 'image/tiff') return 'tiff';
    if (n.endsWith('.ico') || m === 'image/x-icon') return 'ico';
    if (n.endsWith('.heif') || m === 'image/heif') return 'heif';
    if (n.endsWith('.heic') || m === 'image/heic') return 'heic';
    const mm = m.startsWith('image/') ? m.replace('image/','') : '';
    return mm || (n.split('.').pop() || '');
  };
  const mediaCounts = React.useMemo(() => {
    const map: Record<string, number> = { all: 0 };
    for (const key of imageFilterKeys as readonly string[]) map[key] = map[key] || 0;
    for (const it of mediaItems) { const ext = imageExt(it); map.all++; if (ext) map[ext] = (map[ext]||0)+1; }
    return map;
  }, [mediaItems]);
  const pendingInsertIndexRef = useRef<number | null>(null);
  const pendingGroupTargetRef = useRef<string | null>(null);
  const pendingSideRef = useRef<'left'|'right'|null>(null);
  // When opened from settings panels, we should not insert blocks; instead dispatch a 'media-picked' event
  const externalPickRef = useRef<boolean>(false);
  const externalAllowMultipleRef = useRef<boolean>(false);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSlugModal, setShowSlugModal] = useState<boolean>(false);
  const [slugInput, setSlugInput] = useState<string>('');
  const [slugOverwrite, setSlugOverwrite] = useState<boolean>(false);
  const [toolsCollapsed, setToolsCollapsed] = useState<boolean>(false);
  const [seoCollapsed, setSeoCollapsed] = useState<boolean>(false);
  const [blocksCollapsed, setBlocksCollapsed] = useState<boolean>(false);
  const [postsCollapsed, setPostsCollapsed] = useState<boolean>(false);
  const [postSettingsCollapsed, setPostSettingsCollapsed] = useState<boolean>(false);
  const [leftCollapsed, setLeftCollapsed] = useState<boolean>(false);
  const [confirmDeleteSlug, setConfirmDeleteSlug] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [settingsSlug, setSettingsSlug] = useState<string | null>(null);
  const [settingsOrigSlug, setSettingsOrigSlug] = useState<string | null>(null);
  const [settingsModel, setSettingsModel] = useState<ApiPostModel | null>(null);
  const [savingSettings, setSavingSettings] = useState<boolean>(false);
  const [sidebarSettingsSlug, setSidebarSettingsSlug] = useState<string>('');
  // Positioning for the fixed left sidebar toggle
  const leftColRef = useRef<HTMLDivElement | null>(null);
  const leftAsideRef = useRef<HTMLDivElement | null>(null);
  const hdrToolsRef = useRef<HTMLDivElement | null>(null);
  const hdrBlocksRef = useRef<HTMLDivElement | null>(null);
  const hdrSeoRef = useRef<HTMLDivElement | null>(null);
  const hdrPostSettingsRef = useRef<HTMLDivElement | null>(null);
  const hdrPostsRef = useRef<HTMLDivElement | null>(null);
  const toggleBtnRef = useRef<HTMLButtonElement | null>(null);
  const [toggleLeftPx, setToggleLeftPx] = useState<number | null>(null);
  const LAST_EDITED_SLUG_KEY = 'post_editor_last_slug';
  // Multi-page state for the currently loaded post
  const [selectingTitle, setSelectingTitle] = useState<string | null>(null);
  const [pages, setPages] = useState<PageModel[]>([{ title: 'Page 1', blocks: starter.blocks }]);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [rebuildingPage, setRebuildingPage] = useState<boolean>(false);
  // Undo/Redo history at page level
  const historyPastRef = useRef<DisplayPostModel[]>([]);
  const historyFutureRef = useRef<DisplayPostModel[]>([]);
  const MAX_HISTORY = 100;

  // Local draft persistence
  const DRAFT_KEY_PREFIX = 'post_editor_draft_';
  const draftKeyFor = (slug: string | null) => `${DRAFT_KEY_PREFIX}${slug || 'unsaved'}`;
  const saveDraftRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedHashRef = useRef<string>('');
  const autoCommitTimerRef = useRef<number | null>(null);

  const computeStateSnapshot = React.useCallback(() => {
    const liveModel = postRef.current?.getModel?.() || postStateRef.current;
    const snapshot = {
      post: liveModel,
      pages,
      currentPageIndex,
      isDraft,
      historyPast: historyPastRef.current,
      historyFuture: historyFutureRef.current,
      activeBlockId: postRef.current?.getActiveBlockId?.() || null,
      ts: Date.now(),
    } as any;
    return snapshot;
  }, [pages, currentPageIndex, isDraft]);

  const persistDraft = React.useCallback((slug: string | null) => {
    try {
      const key = draftKeyFor(slug);
      const snapshot = computeStateSnapshot();
      const json = JSON.stringify(snapshot);
      // Avoid redundant writes
      const hash = `${json.length}:${(json.charCodeAt(0) || 0) ^ (json.charCodeAt(json.length-1) || 0)}`;
      if (hash === lastSavedHashRef.current) return;
      lastSavedHashRef.current = hash;
      localStorage.setItem(key, json);
    } catch {}
  }, [computeStateSnapshot]);

  const schedulePersist = React.useCallback(() => {
    try { if (saveDraftRef.current) clearTimeout(saveDraftRef.current as any); } catch {}
    saveDraftRef.current = setTimeout(() => persistDraft(currentSlug), 400);
  }, [persistDraft, currentSlug]);

  const loadDraft = React.useCallback((slug: string | null): any | null => {
    try {
      const key = draftKeyFor(slug);
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  }, []);

  // Periodically persist without interrupting typing (no commit)
  useEffect(() => {
    const tick = () => {
      try { persistDraft(currentSlug); } catch {}
    };
    autoCommitTimerRef.current = window.setInterval(tick, 900);
    const onBeforeUnload = () => { try { persistDraft(currentSlug); } catch {} };
    const onVisibility = () => { if (document.visibilityState !== 'visible') { try { persistDraft(currentSlug); } catch {} } };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (autoCommitTimerRef.current) { window.clearInterval(autoCommitTimerRef.current); autoCommitTimerRef.current = null; }
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [schedulePersist, currentSlug]);

  React.useEffect(() => { refreshPosts(); }, []);
  React.useEffect(() => {
    return () => {
      try { setChromeHidden(false); } catch {}
    };
  }, [setChromeHidden]);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const st = await authStatus();
        if (!active) return;
        if (st.ok && st.user) setAuthUser(st.user as AuthUser);
      } catch {}
    })();
    return () => { active = false; };
  }, []);

  // Cross-tab/session guard: re-verify when tab becomes visible or auth cache changes
  useEffect(() => {
    async function recheck() {
      const s = await authStatus();
      if (!s.ok) {
        try {
          const cached = getCachedAuthUser();
          if (cached) {
            setAuthUser(cached);
            return;
          }
        } catch {}
        return;
      }
      setAuthUser(s.user as AuthUser);
      try { if (s.user) setCachedAuthUser(s.user); } catch {}
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') recheck();
    }
    function onStorage(e: StorageEvent) {
      if (e.key === AUTH_CACHE_KEY) recheck();
    }
    recheck();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  async function handleLogout() {
    try { await authLogout(); } catch {}
    setAuthUser(null);
  }
  // On mount, if a last edited slug exists, prefer local draft; else load server
  useEffect(() => {
    const last = localStorage.getItem(LAST_EDITED_SLUG_KEY);
    if (last) {
      (async () => {
        const draft = loadDraft(last) || loadDraft(null);
        if (draft && draft.post) {
          setPost(draft.post);
          setPages(Array.isArray(draft.pages) && draft.pages.length ? draft.pages : [{ title: 'Page 1', blocks: draft.post.blocks || [] }]);
          setCurrentPageIndex(typeof draft.currentPageIndex === 'number' ? draft.currentPageIndex : 0);
          setSelectedPages([]);
          setIsDraft(!!draft.isDraft);
          historyPastRef.current = Array.isArray(draft.historyPast) ? draft.historyPast : [];
          historyFutureRef.current = Array.isArray(draft.historyFuture) ? draft.historyFuture : [];
          postStateRef.current = draft.post;
          setCurrentSlug(last);
          // restore active block caret after content mounts
          const targetId: string | null = draft.activeBlockId || null;
          if (targetId) {
            requestAnimationFrame(() => postRef.current?.setActiveBlockId?.(targetId, 'end'));
          }
        } else {
          const p = await getPost(last);
          if (p) {
            const hasPages = Array.isArray((p as any).pages) && ((p as any).pages as any[]).length > 0;
            const blocks = hasPages ? ((p as any).pages[0]?.blocks || []) : ((p as any).blocks || []);
            const dp: DisplayPostModel = { title: p.title, excerpt: p.excerpt, cover: p.cover, blocks, ...(p as any).navStyle ? { navStyle: (p as any).navStyle } as any : {},
              commentsEnabled: !!(p as any).commentsEnabled,
              seoTitle: (p as any).seoTitle, seoDescription: (p as any).seoDescription, seoKeywords: (p as any).seoKeywords,
              canonicalUrl: (p as any).canonicalUrl, noindex: (p as any).noindex, nofollow: (p as any).nofollow,
              ogImage: (p as any).ogImage, twitterCard: (p as any).twitterCard, generateJsonLd: (p as any).generateJsonLd } as any;
            setPost(dp);
            if (hasPages) {
              const pg = ((p as any).pages as any[]).map((x: any, i: number) => ({ title: x.title || `Page ${i+1}`, blocks: x.blocks || [] }));
              setPages(pg);
              setCurrentPageIndex(0);
            } else {
              setPages([{ title: 'Page 1', blocks }]);
              setCurrentPageIndex(0);
            }
            setSelectedPages([]);
            setCurrentSlug(last);
          }
        }
      })();
    }
  }, []);
  const refreshMarkedCounts = React.useCallback(() => {
    try {
      const marked = postRef.current?.getMarkedLineCount?.() || 0;
      const sel = postRef.current?.getSelectionLineCount?.() || 0;
      setMarkedLines(marked);
      setSelectedLines(sel);
      setHasMarker(!!postRef.current?.hasMarkers?.());
    } catch {
      setMarkedLines(0);
      setSelectedLines(0);
    }
  }, []);

  useEffect(() => {
    const onSel = () => refreshMarkedCounts();
    const onUp = () => refreshMarkedCounts();
    const onKeyUp = () => refreshMarkedCounts();
    document.addEventListener('selectionchange', onSel);
    document.addEventListener('mouseup', onUp, true);
    document.addEventListener('keyup', onKeyUp, true);
    // initial sync
    refreshMarkedCounts();
    // Also schedule persistence to capture latest active block
    const sched = () => schedulePersist();
    document.addEventListener('selectionchange', sched);
    document.addEventListener('mouseup', sched, true);
    document.addEventListener('keyup', sched, true);
    return () => {
      document.removeEventListener('selectionchange', onSel);
      document.removeEventListener('mouseup', onUp, true);
      document.removeEventListener('keyup', onKeyUp, true);
      document.removeEventListener('selectionchange', sched);
      document.removeEventListener('mouseup', sched, true);
      document.removeEventListener('keyup', sched, true);
    };
  }, []);
  // Persist left sidebar collapsed state
  useEffect(() => {
    try {
      const v = localStorage.getItem('left_sidebar_collapsed');
      if (v === '1') setLeftCollapsed(true);
    } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('left_sidebar_collapsed', leftCollapsed ? '1' : '0'); } catch {}
  }, [leftCollapsed]);

  // Persist section collapsed states
  useEffect(() => {
    try {
      const t = localStorage.getItem('left_tools_collapsed');
      if (t === '1') setToolsCollapsed(true);
      const b = localStorage.getItem('left_blocks_collapsed');
      if (b === '1') setBlocksCollapsed(true);
      const s = localStorage.getItem('left_seo_collapsed');
      if (s === '1') setSeoCollapsed(true);
      const ps = localStorage.getItem('left_post_settings_collapsed');
      if (ps === '1') setPostSettingsCollapsed(true);
      const pl = localStorage.getItem('left_posts_collapsed');
      if (pl === '1') setPostsCollapsed(true);
    } catch {}
  }, []);
  // If opened from dashboard (no last edited slug stored), default to only All Posts open
  useEffect(() => {
    try {
      const last = localStorage.getItem(LAST_EDITED_SLUG_KEY);
      if (!last) {
        setToolsCollapsed(true);
        setBlocksCollapsed(true);
        setSeoCollapsed(true);
        setPostSettingsCollapsed(true);
        setPostsCollapsed(false);
        localStorage.setItem('left_tools_collapsed', '1');
        localStorage.setItem('left_blocks_collapsed', '1');
        localStorage.setItem('left_seo_collapsed', '1');
        localStorage.setItem('left_post_settings_collapsed', '1');
        localStorage.setItem('left_posts_collapsed', '0');
      }
    } catch {}
  }, []);
  useEffect(() => { try { localStorage.setItem('left_tools_collapsed', toolsCollapsed ? '1' : '0'); } catch {} }, [toolsCollapsed]);
  useEffect(() => { try { localStorage.setItem('left_blocks_collapsed', blocksCollapsed ? '1' : '0'); } catch {} }, [blocksCollapsed]);
  useEffect(() => { try { localStorage.setItem('left_seo_collapsed', seoCollapsed ? '1' : '0'); } catch {} }, [seoCollapsed]);
  useEffect(() => { try { localStorage.setItem('left_post_settings_collapsed', postSettingsCollapsed ? '1' : '0'); } catch {} }, [postSettingsCollapsed]);
  useEffect(() => { try { localStorage.setItem('left_posts_collapsed', postsCollapsed ? '1' : '0'); } catch {} }, [postsCollapsed]);

  // Reposition the fixed left sidebar toggle to the right edge of the sidebar
  const repositionToggle = React.useCallback(() => {
    const host = leftColRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    // Place the button overlapping the edge by 12px (equivalent to -right-3)
    const left = Math.round(rect.right - 12);
    setToggleLeftPx(left);
  }, []);

  useEffect(() => {
    repositionToggle();
    const onResize = () => repositionToggle();
    const onScroll = () => repositionToggle();
    window.addEventListener('resize', onResize);
    // Capture scrolls from nested containers
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [repositionToggle]);

  useEffect(() => { repositionToggle(); }, [leftCollapsed, repositionToggle]);

  // Open a sidebar section and scroll it into view
  const openSection = (key: 'tools'|'blocks'|'seo'|'postSettings'|'posts') => {
    if (leftCollapsed) setLeftCollapsed(false);
    if (key === 'tools') setToolsCollapsed(false);
    if (key === 'blocks') setBlocksCollapsed(false);
    if (key === 'seo') setSeoCollapsed(false);
    if (key === 'postSettings') setPostSettingsCollapsed(false);
    if (key === 'posts') setPostsCollapsed(false);
    requestAnimationFrame(() => {
      const target = key === 'tools' ? hdrToolsRef.current
        : key === 'blocks' ? hdrBlocksRef.current
        : key === 'seo' ? hdrSeoRef.current
        : key === 'postSettings' ? hdrPostSettingsRef.current
        : hdrPostsRef.current;
      if (target) {
        try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
      }
    });
  };

  // Remember which post is being edited so the editor restores it after reload
  useEffect(() => {
    if (currentSlug) {
      try { localStorage.setItem(LAST_EDITED_SLUG_KEY, currentSlug); } catch {}
    }
  }, [currentSlug]);

  // Keyboard shortcuts for undo/redo; allow native while typing except immediately after an inline style (e.g., color)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      if (e.key.toLowerCase() !== 'z') return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae as any).isContentEditable) {
        if (lastInlineStyleRef.current) {
          // Intercept the very next undo after an inline style so it reverts exactly that step
          e.preventDefault();
          undo();
          lastInlineStyleRef.current = false;
          return;
        }
        // otherwise let native undo operate during typing
        return;
      }
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const refreshPosts = async () => {
    setLoading(true);
    try {
      const items = await listPosts();
      setPosts(items);
    } finally {
      setLoading(false);
    }
  };

  const pushHistory = (prev: DisplayPostModel) => {
    historyPastRef.current.push(JSON.parse(JSON.stringify(prev)) as DisplayPostModel);
    if (historyPastRef.current.length > MAX_HISTORY) historyPastRef.current.shift();
    historyFutureRef.current = [];
  };

  const handleChange = (next: DisplayPostModel) => {
    const prev = postStateRef.current;
    if (JSON.stringify(prev) !== JSON.stringify(next)) pushHistory(prev);
    postStateRef.current = next;
    setPost(next);
    setPages(prev => prev.map((pg, i) => i===currentPageIndex ? { ...pg, blocks: (next as any).blocks || [] } : pg));
    schedulePersist();
  };

  const undo = () => {
    postRef.current?.commitPending();
    if (historyPastRef.current.length === 0) return;
    const prev = historyPastRef.current.pop() as DisplayPostModel;
    historyFutureRef.current.push(JSON.parse(JSON.stringify(post)) as DisplayPostModel);
    postStateRef.current = prev;
    setPost(prev);
    requestAnimationFrame(() => postRef.current?.restoreFocus());
    schedulePersist();
  };

  const redo = () => {
    postRef.current?.commitPending();
    if (historyFutureRef.current.length === 0) return;
    const next = historyFutureRef.current.pop() as DisplayPostModel;
    historyPastRef.current.push(JSON.parse(JSON.stringify(post)) as DisplayPostModel);
    postStateRef.current = next;
    setPost(next);
    requestAnimationFrame(() => postRef.current?.restoreFocus());
    schedulePersist();
  };

  // Apply text color; editor commits immediately and onChange adds a single history entry
  const applyColorWithUndo = (hex: string) => {
    lastInlineStyleRef.current = true;
    postRef.current?.setColor(hex);
  };
  const applyFontSizeWithUndo = (px: number) => {
    const clamped = Math.max(10, Math.min(96, Math.round(px)));
    setFontSize(clamped);
    lastInlineStyleRef.current = true;
    // Restore the editor selection before applying, since the dropdown steals focus
    requestAnimationFrame(() => {
      postRef.current?.restoreFocus();
      requestAnimationFrame(() => {
        postRef.current?.setFontSize(clamped);
      });
    });
  };
  const applyFontFamilyWithUndo = (family: string) => {
    lastInlineStyleRef.current = true;
    // Restore selection because the dropdown steals focus
    requestAnimationFrame(() => {
      postRef.current?.restoreFocus();
      requestAnimationFrame(() => {
        postRef.current?.setFontFamily(family);
      });
    });
  };

  const markSelection = () => {
    // Try to restore focus then mark; update state
    requestAnimationFrame(() => {
      postRef.current?.restoreFocus();
      requestAnimationFrame(() => {
        postRef.current?.addMarker();
        refreshMarkedCounts();
      });
    });
  };
  const eraseMarkers = () => {
    postRef.current?.clearMarkers();
    refreshMarkedCounts();
  };

  const openMediaPicker = async (index?: number, groupTargetId?: string | null, side?: 'left'|'right'|null) => {
    if (typeof index === 'number') pendingInsertIndexRef.current = index; else pendingInsertIndexRef.current = null;
    pendingGroupTargetRef.current = groupTargetId ?? null;
    pendingSideRef.current = side ?? null;
    setMediaOpen(true);
    setMediaLoading(true);
    try {
      const items = await listMedia();
      // Only include images
      const isImage = (it: MediaItem | null | undefined) => !!it && (
        (it.mime || '').startsWith('image/') ||
        ['.jpg','.jpeg','.png','.gif','.webp','.svg','.bmp','.tiff','.ico','.heif','.heic']
          .some(ext => (it?.name || '').toLowerCase().endsWith(ext))
      );
      const onlyImages = items.filter(isImage).sort((a,b)=>b.mtime-a.mtime);
      setMediaItems(onlyImages);
    } catch (e) {
      setMediaItems([]);
    }
    setMediaLoading(false);
  };
  const closeMedia = () => {
    setMediaOpen(false);
    externalPickRef.current = false;
    externalAllowMultipleRef.current = false;
  };
  const onPickImage = (it: MediaItem) => {
    // If picker was opened from settings, return via event and optionally keep open for multi-pick
    if (externalPickRef.current) {
      try { const ev: any = new CustomEvent('media-picked', { detail: { urls: [it.url] } }); window.dispatchEvent(ev); } catch {}
      if (!externalAllowMultipleRef.current) closeMedia();
      return;
    }
    closeMedia();
    // Insert image block respecting pending row/side if provided
    requestAnimationFrame(() => {
      const idx = pendingInsertIndexRef.current;
      const gid = pendingGroupTargetRef.current;
      const side = pendingSideRef.current;
      if (typeof idx === 'number') {
        if (gid) {
          postRef.current?.insertImageAtWithGroup?.(it.url, idx, gid, side ?? null);
        } else {
          postRef.current?.insertImageAt?.(it.url, idx);
        }
      } else {
        postRef.current?.insertImage?.(it.url);
      }
      pendingInsertIndexRef.current = null;
      pendingGroupTargetRef.current = null;
      pendingSideRef.current = null;
    });
    // If there's no cover yet, use the chosen image as cover immediately
    setPost(prev => {
      if (!prev.cover) return { ...prev, cover: it.url } as DisplayPostModel;
      return prev;
    });
  };

  useEffect(() => {
    const onOpen = (e: Event) => {
      const anyE = e as any;
      const idx: number | undefined = anyE?.detail?.index;
      const gid: string | undefined = anyE?.detail?.groupTargetId;
      const side: 'left'|'right'|undefined = anyE?.detail?.side;
      const multiple: boolean | undefined = anyE?.detail?.multiple;
      // If called without index/targets but with 'multiple' flag, assume external settings request
      if (typeof idx !== 'number' && !gid && typeof multiple !== 'undefined') {
        externalPickRef.current = true;
        externalAllowMultipleRef.current = !!multiple;
      } else {
        externalPickRef.current = false;
        externalAllowMultipleRef.current = false;
      }
      openMediaPicker(typeof idx === 'number' ? idx : undefined, gid ?? null, side ?? null);
    };
    window.addEventListener('open-media-picker', onOpen as any);
    return () => window.removeEventListener('open-media-picker', onOpen as any);
  }, []);

  const slugify = (s: string) => s.toLowerCase().trim().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,'');
  const isSlugTaken = (s: string) => posts.some(p => p.slug === s);
  const suggestUniqueSlug = (base: string) => {
    const clean = slugify(base);
    if (!isSlugTaken(clean)) return clean;
    let i = 1;
    while (isSlugTaken(`${clean}_${i}`)) i++;
    return `${clean}_${i}`;
  };

  // Keep sidebar Post Settings slug in sync:
  // - For new posts (no currentSlug), derive from title
  // - For existing posts, keep user's slug field as-is, only initialize once from currentSlug
  useEffect(() => {
    if (currentSlug) {
      setSidebarSettingsSlug(prev => prev || currentSlug);
    } else {
      setSidebarSettingsSlug(slugify(post.title || ''));
    }
  }, [currentSlug, post.title]);

  const performSaveWithSlug = async (slug: string, opts?: { renameFrom?: string | null }) => {
    setIsSaving(true);
    try {
      // Pull the freshest model from the editor (includes layout maps), fallback to local state
      const latest: any = (postRef.current?.getModel?.() as any) || post;
      const payload: any = { ...latest, draft: !!isDraft };
      // Ensure commentsEnabled is always present in JSON as a boolean for consistency
      payload.commentsEnabled = !!(post as any).commentsEnabled;
      payload.pages = pages.map(p => ({ title: p.title, blocks: p.blocks }));
      payload.blocks = pages[currentPageIndex]?.blocks || [];
      if (!payload.cover) {
        try {
          const firstImg = (pages.flatMap(p => p.blocks) || []).find((b: any) => b && b.type === 'image' && b.src);
          if (firstImg && firstImg.src) payload.cover = firstImg.src;
        } catch {}
      }
      await savePost(slug, payload);
      setCurrentSlug(slug);
      // Move draft to the saved slug and persist fresh snapshot
      try {
        const oldKey = draftKeyFor(opts?.renameFrom || null);
        const newKey = draftKeyFor(slug);
        const snapshot = computeStateSnapshot();
        localStorage.setItem(newKey, JSON.stringify(snapshot));
        if (oldKey !== newKey) localStorage.removeItem(oldKey);
      } catch {}
      // Reflect any auto-assigned cover locally so Post Settings shows it immediately
      if (payload.cover && post.cover !== payload.cover) {
        setPost(prev => ({ ...prev, cover: payload.cover }));
      }
      refreshPosts();
      setShowSlugModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Persist when key editor pieces change (debounced)
  useEffect(() => { schedulePersist(); }, [pages, currentPageIndex, isDraft, post]);

  const doSave = async () => {
    // Base slug preference: sidebar field, then derived from title
    const titleSlug = slugify(post.title || '');
    let slug = (sidebarSettingsSlug || '').trim() || titleSlug || 'untitled';

    // Immediate local check: prefer title-derived slug for the popup if it differs from sidebar slug
    const conflictSlug = (titleSlug && titleSlug !== slug) ? titleSlug : slug;
    if (isSlugTaken(conflictSlug) && conflictSlug !== currentSlug) {
      setSlugInput(conflictSlug);
      setSlugOverwrite(true);
      setShowSlugModal(true);
      return;
    }

    // If editing an existing post and user hasn't changed the slug field (still equal to currentSlug),
    // but the title implies a different slug that does not yet exist, treat as a new post save.
    if (currentSlug && slug === currentSlug && titleSlug && titleSlug !== currentSlug) {
      const titleExists = await postExists(titleSlug);
      if (!titleExists) {
        slug = titleSlug; // create a new folder/post
      } else {
        // Title maps to an existing post name — prompt before saving
        setSlugInput(titleSlug);
        setSlugOverwrite(true);
        setShowSlugModal(true);
        return;
      }
    }

    // Server-verified existence to avoid stale client lists
    const exists = slug !== currentSlug ? await postExists(slug) : false;
    if (exists) {
      setSlugInput(slug);
      setSlugOverwrite(true);
      setShowSlugModal(true);
      return;
    }

    const renameFrom = currentSlug && currentSlug !== slug ? currentSlug : null;
    await performSaveWithSlug(slug, { renameFrom });
  };

  const openPreview = () => {
    try {
      postRef.current?.commitPending?.();
      const id = Math.random().toString(36).slice(2, 10);
      const current = (postRef.current?.getModel?.() as any) || postStateRef.current || post;
      localStorage.setItem(`post_preview_${id}` , JSON.stringify({ post: current, isDraft }));
      const url = `/quill-admin/preview?id=${encodeURIComponent(id)}`;
      window.open(url, '_blank', 'noreferrer');
    } catch {}
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
              title="Back to Admin"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className="text-sm font-semibold">Admin</span>
            </Link>
            <span className="font-semibold text-gray-900">Posts</span>
            <button
              type="button"
              onClick={() => setChromeHidden(!chromeHidden)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition"
              title={chromeHidden ? 'Show header and navigation bar' : 'Hide header and navigation bar'}
              aria-label={chromeHidden ? 'Show header and navigation bar' : 'Hide header and navigation bar'}
            >
              {chromeHidden ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              )}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-editor-action
              onMouseDown={(e)=>{ e.preventDefault(); }}
              onClick={undo}
              disabled={historyPastRef.current.length === 0}
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo (Ctrl/Cmd+Z)"
            >Undo</button>
            <button
              data-editor-action
              onMouseDown={(e)=>{ e.preventDefault(); }}
              onClick={redo}
              disabled={historyFutureRef.current.length === 0}
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo (Ctrl/Cmd+Shift+Z)"
            >Redo</button>
            <input
              value={post.title}
              onChange={(e) => setPost(p => ({ ...p, title: e.target.value }))}
              className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Post title"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 select-none">
              <input type="checkbox" checked={isDraft} onChange={(e)=> setIsDraft(e.target.checked)} />
              As draft
            </label>
            <button
              onClick={doSave}
              disabled={isSaving}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors ${isSaving ? 'bg-gray-700' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              {isSaving ? (
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M22 12a10 10 0 0 1-10 10" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8"/><path d="M7 3v5h8"/></svg>
              )}
              <span>{isSaving ? 'Saving…' : 'Save'}</span>
            </button>
            <button onClick={openPreview} className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview
            </button>
            {authUser ? (
              <UserMenu user={authUser} onLogout={handleLogout} />
            ) : null}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid gap-6" style={{ gridTemplateColumns: `${leftCollapsed ? '56px' : '360px'} minmax(0,1fr)` }}>
        <div className="relative" ref={leftColRef}>
          <aside ref={leftAsideRef} className={`rounded-xl border border-gray-200 bg-white shadow-sm overflow-y-auto ${leftCollapsed ? 'p-2' : 'p-4'} space-y-5 transition-all duration-300`}>
          <div className="space-y-3">
            <div className="flex items-center justify-between" ref={hdrToolsRef}>
              <div className="flex items-center gap-2 font-semibold text-gray-900" title="Tools">
                <button
                  type="button"
                  onClick={() => openSection('tools')}
                  className={`inline-flex items-center justify-center rounded-md bg-gradient-to-br from-purple-600 to-blue-600 text-white transition-all ${leftCollapsed ? 'h-8 w-8' : 'h-5 w-5'} hover:shadow-lg hover:brightness-110 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-400/60`}
                  aria-label="Open Tools section"
                >
                  <svg viewBox="0 0 24 24" className={leftCollapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 1 1.4 0l1.6 1.6a1 1 0 0 1 0 1.4L9 18H6v-3l8.7-8.7z"/><path d="M16 5l3 3"/></svg>
                </button>
                <span className={`${leftCollapsed ? 'sr-only' : ''}`}>Tools</span>
              </div>
              {!leftCollapsed && (
              <button
                onClick={()=> setToolsCollapsed(v => !v)}
                aria-label={toolsCollapsed ? 'Expand tools' : 'Collapse tools'}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm transition"
                title={toolsCollapsed ? 'Expand' : 'Collapse'}
              >
                {toolsCollapsed ? (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                )}
              </button>
              )}
            </div>
            <div className={`${leftCollapsed || toolsCollapsed ? 'hidden' : ''} rounded-lg border border-gray-200 p-3 space-y-3`}>
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-600 tracking-wide">Arrow keys on selection</div>
                <div className="flex items-center gap-3" data-editor-action>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="sel-arrow-mode"
                      value="nothing"
                      checked={selectionArrowMode === 'nothing'}
                      onChange={()=> setSelectionArrowMode('nothing')}
                    />
                    Nothing
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="sel-arrow-mode"
                      value="move"
                      checked={selectionArrowMode === 'move'}
                      onChange={()=> setSelectionArrowMode('move')}
                    />
                    Move block
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-600 tracking-wide">Alignment</div>
                <div className="flex items-center gap-2" data-editor-action>
                  <button
                    title="Align left"
                    onMouseDown={(e)=>{ e.preventDefault(); postRef.current?.restoreFocus(); requestAnimationFrame(()=> postRef.current?.inline('justifyLeft')); }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 w-9"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M3 12h12"/><path d="M3 18h18"/></svg>
                  </button>
                  <button
                    title="Align center"
                    onMouseDown={(e)=>{ e.preventDefault(); postRef.current?.restoreFocus(); requestAnimationFrame(()=> postRef.current?.inline('justifyCenter')); }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 w-9"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6h12"/><path d="M3 12h18"/><path d="M6 18h12"/></svg>
                  </button>
                  <button
                    title="Align right"
                    onMouseDown={(e)=>{ e.preventDefault(); postRef.current?.restoreFocus(); requestAnimationFrame(()=> postRef.current?.inline('justifyRight')); }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 w-9"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M9 12h12"/><path d="M3 18h18"/></svg>
                  </button>
                  <button
                    title="Justify"
                    onMouseDown={(e)=>{ e.preventDefault(); postRef.current?.restoreFocus(); requestAnimationFrame(()=> postRef.current?.inline('justifyFull')); }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 w-9"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M3 12h18"/><path d="M3 18h18"/></svg>
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="text-xs font-semibold text-gray-600 tracking-wide">Inline</div>
                <div className="flex flex-wrap gap-2">
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.inline('bold');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Bold</button>
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.inline('italic');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Italic</button>
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.inline('underline');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Underline</button>
                </div>
                <div className="flex items-center gap-2" data-editor-action>
                  <button
                    title="Bulleted list"
                    onMouseDown={(e)=>{ e.preventDefault(); postRef.current?.restoreFocus(); requestAnimationFrame(()=> postRef.current?.inline('insertUnorderedList')); }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 px-3 text-sm"
                  >
                    • List
                  </button>
                  <button
                    title="Numbered list"
                    onMouseDown={(e)=>{ e.preventDefault(); postRef.current?.restoreFocus(); requestAnimationFrame(()=> postRef.current?.inline('insertOrderedList')); }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 px-3 text-sm"
                  >
                    1. List
                  </button>
                </div>
                <div className="mt-1 flex items-center gap-2" data-editor-action>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-700 select-none rounded-full border border-gray-200 bg-white px-3 py-1 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={autoBullet}
                      onChange={(e)=>{
                        const v = e.target.checked;
                        setAutoBullet(v);
                        if (v && autoNumber) { setAutoNumber(false); try { postRef.current?.setAutoNumber?.(false); } catch {} }
                        try { postRef.current?.setAutoBullet?.(v); } catch {}
                        // Restore editor focus/caret for immediate Enter behavior
                        try { requestAnimationFrame(()=> postRef.current?.restoreFocus?.()); } catch {}
                      }}
                    />
                    <span>Auto bullet on Enter</span>
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-700 select-none rounded-full border border-gray-200 bg-white px-3 py-1 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={autoNumber}
                      onChange={(e)=>{
                        const v = e.target.checked;
                        setAutoNumber(v);
                        if (v && autoBullet) { setAutoBullet(false); try { postRef.current?.setAutoBullet?.(false); } catch {} }
                        try { postRef.current?.setAutoNumber?.(v); } catch {}
                        // Restore editor focus/caret for immediate Enter behavior
                        try { requestAnimationFrame(()=> postRef.current?.restoreFocus?.()); } catch {}
                      }}
                    />
                    <span>Auto number on Enter</span>
                  </label>
                </div>
                {/* Marked/Selected lines label */}
                <div className="mt-1 flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 text-purple-700 px-3 py-1 text-xs font-medium select-none">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h10"/></svg>
                    <span>
                      {markedLines > 0
                        ? `${markedLines} marked ${markedLines === 1 ? 'line' : 'lines'}`
                        : `${selectedLines} selected ${selectedLines === 1 ? 'line' : 'lines'}`}
                    </span>
                  </span>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-700 select-none rounded-full border border-gray-200 bg-white px-3 py-1 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={applyToSelectionOnly}
                      onChange={(e)=>{ const v = e.target.checked; setApplyToSelectionOnly(v); try { postRef.current?.setApplyToSelectionOnly?.(v); } catch {} }}
                    />
                    <span>Apply tools to selected lines</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-editor-action
                    onMouseDown={(e)=>{ e.preventDefault(); markSelection(); }}
                    className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 text-sm"
                    title="Mark selection"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-10 10H2v-10L12 2l3 3-7 7"/></svg>
                    Mark selection
                  </button>
                  {hasMarker && (
                    <button
                      data-editor-action
                      onMouseDown={(e)=>{ e.preventDefault(); eraseMarkers(); }}
                      className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 px-3 py-1.5 text-sm"
                      title="Erase marker"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
                      Erase marker
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-600 tracking-wide">Fonts</div>
                  <div className="flex items-center gap-2" data-editor-action>
                    <select
                      value={fontFamily}
                      onChange={(e)=>{ const v=e.target.value; setFontFamily(v); if (v) applyFontFamilyWithUndo(v); }}
                      className="rounded-md border border-gray-200 px-2 py-1.5 text-sm bg-white"
                    >
                      <option value="">Select font…</option>
                      <option value="Inter" style={{ fontFamily: 'Inter' }}>Inter</option>
                      <option value="Georgia" style={{ fontFamily: 'Georgia' }}>Georgia</option>
                      <option value="Times New Roman" style={{ fontFamily: 'Times New Roman' }}>Times New Roman</option>
                      <option value="Garamond" style={{ fontFamily: 'Garamond' }}>Garamond</option>
                      <option value="Merriweather" style={{ fontFamily: 'Merriweather' }}>Merriweather</option>
                      <option value="Playfair Display" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</option>
                      <option value="Lora" style={{ fontFamily: 'Lora' }}>Lora</option>
                      <option value="Roboto" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                      <option value="Source Sans Pro" style={{ fontFamily: 'Source Sans Pro' }}>Source Sans Pro</option>
                      <option value="Open Sans" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                      <option value="Montserrat" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                      <option value="Poppins" style={{ fontFamily: 'Poppins' }}>Poppins</option>
                      <option value="Raleway" style={{ fontFamily: 'Raleway' }}>Raleway</option>
                      <option value="Courier New" style={{ fontFamily: 'Courier New' }}>Courier New</option>
                      <option value="Fira Code" style={{ fontFamily: 'Fira Code' }}>Fira Code</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-600 tracking-wide">Font size</div>
                  <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-center" data-editor-action>
                    <button
                      title="Decrease font size"
                      onMouseDown={(e)=>{ e.preventDefault(); applyFontSizeWithUndo(fontSize - 2); }}
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 w-9"
                    >
                      <span className="font-semibold" style={{ fontSize: 14 }}>A</span>
                    </button>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={10}
                        max={96}
                        value={fontSize}
                        onChange={(e)=>{ const v = parseInt(e.target.value || '0', 10); setFontSize(isNaN(v)?16:v); }}
                        onBlur={()=> applyFontSizeWithUndo(fontSize)}
                        className="w-24 rounded-md border border-gray-200 px-2 py-1.5 text-sm"
                      />
                      <span className="text-xs text-gray-500">px</span>
                      <select
                        value={fontSize}
                        onChange={(e)=> applyFontSizeWithUndo(parseInt(e.target.value,10))}
                        className="rounded-md border border-gray-200 px-2 py-1.5 text-sm bg-white"
                      >
                        {[12,14,16,18,20,24,30,36,48,60,72].map(v => (
                          <option key={v} value={v}>{v} px</option>
                        ))}
                      </select>
                    </div>
                    <button
                      title="Increase font size"
                      onMouseDown={(e)=>{ e.preventDefault(); applyFontSizeWithUndo(fontSize + 2); }}
                      className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 h-9 w-9"
                    >
                      <span className="font-semibold" style={{ fontSize: 18 }}>A</span>
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-600 tracking-wide">Text color</div>
                  <div className="rounded-lg border border-gray-200 p-2 bg-white" data-editor-action>
                    <HexColorPicker color={color} onChange={(c: string)=>{ setColor(c); }} />
                  </div>
                  <input
                    data-editor-action
                    value={color}
                    onChange={(e)=>{ setColor(e.target.value); }}
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <button data-editor-action
                      onMouseDown={(e)=>{ e.preventDefault(); applyColorWithUndo(color); }}
                      className="inline-flex items-center justify-center rounded-md bg-gray-900 text-white text-sm px-3 py-1.5 hover:bg-gray-800"
                    >Apply</button>
                    <span className="inline-flex h-6 w-6 rounded border border-gray-300" style={{ backgroundColor: color }} />
                  </div>
                  <div className="mt-3 grid grid-cols-5 gap-2">
                    {[
                      '#000000','#111827','#374151','#6B7280','#9CA3AF',
                      '#F3F4F6','#FFFFFF','#EF4444','#F97316','#F59E0B',
                      '#EAB308','#84CC16','#22C55E','#10B981','#06B6D4',
                      '#0EA5E9','#3B82F6','#6366F1','#8B5CF6','#A855F7',
                      '#D946EF','#EC4899','#F43F5E','#FB7185','#FDA4AF',
                      '#FCA5A5','#FDE68A','#A7F3D0','#93C5FD','#C7D2FE',
                      '#DDD6FE','#FBCFE8','#F5D0FE','#34D399','#14B8A6','#60A5FA',
                      '#F59E0B','#EA580C','#065F46','#052e16'
                    ].map((c)=> (
                      <button
                        key={c}
                        data-editor-action
                        title={c}
                        onMouseDown={(e)=>{ e.preventDefault(); applyColorWithUndo(c); }}
                        className="h-8 w-8 rounded-md border border-gray-200 hover:scale-105 transition-transform"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Block style</div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('paragraph');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Paragraph</button>
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('quote');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Quote</button>
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('heading1');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Heading 1</button>
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('heading2');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Heading 2</button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Insert</div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); openMediaPicker();}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Image</button>
                  <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('code');}} className="px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-sm">Code</button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between" ref={hdrBlocksRef}>
            <div className="flex items-center gap-2 font-semibold text-gray-900" title="Blocks">
              <button
                type="button"
                onClick={() => openSection('blocks')}
                className={`inline-flex items-center justify-center rounded-md bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white transition-all ${leftCollapsed ? 'h-8 w-8' : 'h-5 w-5'} hover:shadow-lg hover:brightness-110 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/60`}
                aria-label="Open Blocks section"
              >
                <svg viewBox="0 0 24 24" className={leftCollapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </button>
              <span className={`${leftCollapsed ? 'sr-only' : ''}`}>Blocks</span>
            </div>
            {!leftCollapsed && (
            <button
              onClick={()=> setBlocksCollapsed(v => !v)}
              aria-label={blocksCollapsed ? 'Expand blocks' : 'Collapse blocks'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm transition"
              title={blocksCollapsed ? 'Expand' : 'Collapse'}
            >
              {blocksCollapsed ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              )}
            </button>
            )}
          </div>
          <div className={`${leftCollapsed || blocksCollapsed ? 'hidden' : ''} rounded-lg border border-gray-200 p-3`}>
            <div className="grid grid-cols-2 gap-2">
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('paragraph');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-indigo-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-indigo-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15"/><path d="M4 4.5h10a4 4 0 0 1 0 8H8"/></svg>
                </span>
                Paragraph
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('quote');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-emerald-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 6H7a1 1 0 0 0-1 1v10l4-3h7a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z"/></svg>
                </span>
                Quote
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('heading1');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-amber-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-amber-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6v12"/><path d="M12 6v12"/><path d="M4 12h8"/><path d="M18 6v12"/></svg>
                </span>
                Heading 1
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.block('heading2');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-sky-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-sky-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6v12"/><path d="M12 6v12"/><path d="M4 12h8"/><path d="M18 10h2a2 2 0 1 1 0 4h-2"/></svg>
                </span>
                Heading 2
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); openMediaPicker();}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-rose-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-rose-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                </span>
                Image
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('code');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-900 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                </span>
                Code
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('timeline');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-purple-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-purple-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M6 12h12"/><path d="M3 18h18"/></svg>
                </span>
                Timeline
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('carousel');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-fuchsia-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-fuchsia-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M8 6v12"/><path d="M16 6v12"/></svg>
                </span>
                Carousel
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('faq');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-violet-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-violet-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </span>
                FAQ
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('table');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-teal-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-teal-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M15 3v18"/><path d="M3 9h18"/><path d="M3 15h18"/></svg>
                </span>
                Table
              </button>
              <button data-editor-action onMouseDown={(e)=>{e.preventDefault(); postRef.current?.insert('embed');}} className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-cyan-50">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-cyan-600 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><rect x="9" y="4" width="6" height="16" rx="1"/></svg>
                </span>
                HTML Embed
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between" ref={hdrSeoRef}>
            <div className="flex items-center gap-2 font-semibold text-gray-900" title="SEO Tools">
              <button
                type="button"
                onClick={() => openSection('seo')}
                className={`inline-flex items-center justify-center rounded-md bg-gradient-to-br from-emerald-600 to-teal-600 text-white transition-all ${leftCollapsed ? 'h-8 w-8' : 'h-5 w-5'} hover:shadow-lg hover:brightness-110 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-400/60`}
                aria-label="Open SEO Tools section"
              >
                <svg viewBox="0 0 24 24" className={leftCollapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </button>
              <span className={`${leftCollapsed ? 'sr-only' : ''}`}>SEO Tools</span>
            </div>
            {!leftCollapsed && (
            <button
              onClick={()=> setSeoCollapsed(v => !v)}
              aria-label={seoCollapsed ? 'Expand SEO tools' : 'Collapse SEO tools'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm transition"
              title={seoCollapsed ? 'Expand' : 'Collapse'}
            >
              {seoCollapsed ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              )}
            </button>
            )}
          </div>
          <div className={`${leftCollapsed || seoCollapsed ? 'hidden' : ''} rounded-lg border border-gray-200 p-3`}>
            <SeoTools
              value={post as any}
              slug={currentSlug || sidebarSettingsSlug}
              onChange={(patch)=> setPost(prev => ({ ...(prev as any), ...(patch as any) } as any))}
            />
          </div>

          <div className="flex items-center justify-between" ref={hdrPostSettingsRef}>
            <div className="flex items-center gap-2 font-semibold text-gray-900" title="Post Settings">
              <button
                type="button"
                onClick={() => openSection('postSettings')}
                className={`inline-flex items-center justify-center rounded-md bg-gradient-to-br from-purple-600 to-blue-600 text-white transition-all ${leftCollapsed ? 'h-8 w-8' : 'h-5 w-5'} hover:shadow-lg hover:brightness-110 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-400/60`}
                aria-label="Open Post Settings section"
              >
                <svg viewBox="0 0 24 24" className={leftCollapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.6 15a1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.6a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.4 8c0 .57.22 1.12.6 1.51.39.39.91.6 1.51.6H23a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
              </button>
              <span className={`${leftCollapsed ? 'sr-only' : ''}`}>Post Settings</span>
            </div>
            {!leftCollapsed && (
            <button
              onClick={()=> setPostSettingsCollapsed(v => !v)}
              aria-label={postSettingsCollapsed ? 'Expand post settings' : 'Collapse post settings'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm transition"
              title={postSettingsCollapsed ? 'Expand' : 'Collapse'}
            >
              {postSettingsCollapsed ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              )}
            </button>
            )}
          </div>
          <div className={`${leftCollapsed || postSettingsCollapsed ? 'hidden' : ''} rounded-lg border border-gray-200 p-3`}>
            <PostSettings
              value={post as any}
              slug={sidebarSettingsSlug}
              onChange={(m, extras)=> {
                const withBlocks = (m as any).blocks ? (m as any).blocks : post.blocks;
                setPost({ ...(post as any), ...(m as any), blocks: withBlocks } as DisplayPostModel);
                if (typeof (m as any).draft !== 'undefined') setIsDraft(!!(m as any).draft);
                if (extras?.slug !== undefined) setSidebarSettingsSlug(extras.slug);
              }}
            />
          </div>

          <div className="flex items-center justify-between" ref={hdrPostsRef}>
            <div className="flex items-center gap-2 font-semibold text-gray-900" title="All Posts">
              <button
                type="button"
                onClick={() => openSection('posts')}
                className={`inline-flex items-center justify-center rounded-md bg-gradient-to-br from-purple-600 to-blue-600 text-white transition-all ${leftCollapsed ? 'h-8 w-8' : 'h-5 w-5'} hover:shadow-lg hover:brightness-110 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-400/60`}
                aria-label="Open All Posts section"
              >
                <svg viewBox="0 0 24 24" className={leftCollapsed ? 'h-5 w-5' : 'h-3.5 w-3.5'} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/></svg>
              </button>
              <span className={`${leftCollapsed ? 'sr-only' : ''}`}>All Posts</span>
            </div>
            {!leftCollapsed && (
            <button
              onClick={()=> setPostsCollapsed(v => !v)}
              aria-label={postsCollapsed ? 'Expand posts list' : 'Collapse posts list'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm transition"
              title={postsCollapsed ? 'Expand' : 'Collapse'}
            >
              {postsCollapsed ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              )}
            </button>
            )}
          </div>
          <div className={`${leftCollapsed || postsCollapsed ? 'hidden' : ''} rounded-lg border border-gray-200 bg-white max-h-96 overflow-y-auto divide-y divide-gray-100`}>
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Loading posts…</div>
            ) : posts.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No posts yet</div>
            ) : (
              posts.sort((a,b)=>b.mtime-a.mtime).map(it => (
                <div key={it.slug} className={`px-3 py-2 ${currentSlug===it.slug ? 'bg-purple-50' : ''}`}>
                  <div className="flex items-center justify-between gap-2">
                  <button
                    className="flex-1 min-w-0 text-left text-sm"
                    onClick={async () => {
                      setSelectingTitle(it.title || it.slug);
                      let p: any = null;
                      try {
                        p = await getPost(it.slug);
                      } finally {
                        // we will clear selectingTitle after state is applied below to avoid flash
                      }
                      if (p) {
                        historyPastRef.current = [];
                        historyFutureRef.current = [];
                        const hasPages = Array.isArray((p as any).pages) && ((p as any).pages as any[]).length > 0;
                        const blocks = hasPages ? ((p as any).pages[0]?.blocks || []) : ((p as any).blocks || []);
                        const dp: DisplayPostModel = { title: p.title, excerpt: p.excerpt, cover: p.cover, blocks, ...(p as any).navStyle ? { navStyle: (p as any).navStyle } as any : {},
                          commentsEnabled: !!(p as any).commentsEnabled,
                          seoTitle: (p as any).seoTitle, seoDescription: (p as any).seoDescription, seoKeywords: (p as any).seoKeywords,
                          canonicalUrl: (p as any).canonicalUrl, noindex: (p as any).noindex, nofollow: (p as any).nofollow,
                          ogImage: (p as any).ogImage, twitterCard: (p as any).twitterCard, generateJsonLd: (p as any).generateJsonLd } as any;
                        setPost(dp);
                        if (hasPages) {
                          const pg = ((p as any).pages as any[]).map((x: any, i: number) => ({ title: x.title || `Page ${i+1}`, blocks: x.blocks || [] }));
                          setPages(pg);
                          setCurrentPageIndex(0);
                        } else {
                          setPages([{ title: 'Page 1', blocks }]);
                          setCurrentPageIndex(0);
                        }
                        setSelectedPages([]);
                        setCurrentSlug(it.slug);
                        setSelectingTitle(null);
                      }
                    }}
                  >
                    <div className="font-medium text-gray-900 whitespace-nowrap truncate leading-snug">{it.title || it.slug}</div>
                    <div className="text-xs text-gray-500">{new Date(it.mtime*1000).toLocaleString()}</div>
                  </button>
                  <div className="shrink-0 flex items-center gap-1">
                    <button
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-200 px-2 text-xs text-gray-700 hover:bg-gray-50"
                      title="Edit settings"
                      onClick={async () => {
                        const p = await getPost(it.slug);
                        if (p) {
                          setSettingsOrigSlug(it.slug);
                          setSettingsSlug(it.slug);
                          setSettingsModel(p);
                          setSettingsOpen(true);
                        }
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                      Edit
                    </button>
                    <a
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-200 px-2 text-xs text-gray-700 hover:bg-gray-50"
                      title="Open published post"
                      href={`/posts/${it.slug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      View
                    </a>
                    <button
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                      title="Delete"
                      onClick={() => setConfirmDeleteSlug(it.slug)}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                  </div>
                  {currentSlug === it.slug && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-gray-600 tracking-wide">Pages</div>
                        {selectedPages.length === 0 && pages.length > 0 && (
                          <button
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
                            onClick={() => setSelectedPages(pages.map((_,i)=>i))}
                            title="Select all pages"
                          >
                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            Select all
                          </button>
                        )}
                      </div>
                      {rebuildingPage && (
                        <div className="flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-purple-800 text-xs">
                          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M22 12a10 10 0 0 1-10 10" /></svg>
                          Rebuilding first page…
                        </div>
                      )}
                      {selectedPages.length > 0 && !rebuildingPage && (
                        <div className="flex items-center justify-between rounded-md border border-purple-200 bg-purple-50 px-3 py-2">
                          <div className="text-xs text-purple-800">{selectedPages.length} selected</div>
                          <div className="flex items-center gap-2">
                            <button
                              className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                              onClick={() => setSelectedPages([])}
                            >Clear</button>
                            <button
                              className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 text-red-700 px-2.5 py-1 text-xs hover:bg-red-100"
                              onClick={() => {
                                postRef.current?.commitPending?.();
                                setPages(ps => {
                                  const keep = ps.filter((_,i)=> !selectedPages.includes(i));
                                  if (keep.length === 0) {
                                    setRebuildingPage(true);
                                    setSelectedPages([]);
                                    setTimeout(() => {
                                      const template = [
                                        { id: Math.random().toString(36).slice(2, 10), type: 'heading1', text: 'Your amazing headline — Page 1' },
                                        { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Start writing your story here. You can make text bold, italic, add quotes, images and code blocks. Everything updates live.' },
                                        { id: Math.random().toString(36).slice(2, 10), type: 'quote', text: 'Simplicity is the soul of efficiency.' },
                                        { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Press the toolbar buttons to change styles or insert blocks. Click anywhere and type freely.' },
                                      ];
                                      const created = [{ title: 'Page 1', blocks: template }];
                                      setPages(created);
                                      setCurrentPageIndex(0);
                                      setPost(p => ({ ...p, blocks: template } as any));
                                      postStateRef.current = { ...postStateRef.current, blocks: template } as any;
                                      setRebuildingPage(false);
                                    }, 450);
                                    return [] as any;
                                  } else {
                                    let nextIndex = currentPageIndex;
                                    if (selectedPages.includes(currentPageIndex)) {
                                      const sorted = [...selectedPages].sort((a,b)=>a-b);
                                      const minDel = sorted[0] ?? 0;
                                      nextIndex = Math.max(0, Math.min(minDel, keep.length-1));
                                    }
                                    setCurrentPageIndex(nextIndex);
                                    const blk = keep[nextIndex]?.blocks || [];
                                    setPost(p => ({ ...p, blocks: blk } as any));
                                    postStateRef.current = { ...postStateRef.current, blocks: blk } as any;
                                    setSelectedPages([]);
                                    return keep;
                                  }
                                });
                              }}
                            >
                              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                              Delete selected
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        {pages.map((pg, idx) => (
                          <div key={idx} className={`flex items-center gap-2 rounded-md border p-2 transition ${currentPageIndex===idx ? 'border-purple-300 bg-purple-50 ring-2 ring-purple-300/60 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}>
                            <button
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-md ring-1 ${selectedPages.includes(idx) ? 'bg-purple-600 text-white ring-purple-600' : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'}`}
                              title={selectedPages.includes(idx) ? 'Deselect' : 'Select'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPages(prev => prev.includes(idx) ? prev.filter(i=>i!==idx) : [...prev, idx]);
                              }}
                            >
                              {selectedPages.includes(idx) ? (
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" className="h-4 w-4 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
                              )}
                            </button>
                            <button
                              className="flex-1 min-w-0 text-left text-sm truncate"
                              title={pg.title || `Page ${idx+1}`}
                              onClick={() => {
                                // Commit any pending edits, then persist current page blocks
                                postRef.current?.commitPending?.();
                                const currentBlocks = (postStateRef.current as any)?.blocks || post.blocks || [];
                                if (currentPageIndex !== idx) {
                                  setPages(ps => {
                                    const next = ps.slice();
                                    if (next[currentPageIndex]) {
                                      next[currentPageIndex] = { ...next[currentPageIndex], blocks: currentBlocks } as any;
                                    }
                                    return next;
                                  });
                                }
                                const targetBlocks = pages[idx]?.blocks || [];
                                setCurrentPageIndex(idx);
                                setPost(p => ({ ...p, blocks: targetBlocks } as any));
                                postStateRef.current = { ...postStateRef.current, blocks: targetBlocks } as any;
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-semibold ring-1 ${currentPageIndex===idx ? 'bg-purple-100 text-purple-700 ring-purple-200' : 'bg-gray-100 text-gray-700 ring-gray-200'}`}>{idx+1}</span>
                                <span className="truncate">{pg.title || `Page ${idx+1}`}</span>
                              </div>
                            </button>
                            <div className="flex items-center gap-1">
                              <button
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                title="Move up"
                                disabled={idx===0}
                                onClick={() => {
                                  if (idx===0) return;
                                  postRef.current?.commitPending?.();
                                  setPages(ps => {
                                    const next = ps.slice();
                                    const t = next[idx-1]; next[idx-1] = next[idx]; next[idx] = t;
                                    // keep current selection with the moved page
                                    if (currentPageIndex === idx) setCurrentPageIndex(idx-1);
                                    else if (currentPageIndex === idx-1) setCurrentPageIndex(idx);
                                    // sync blocks to editor
                                    const blk = next[Math.min(currentPageIndex === idx ? idx-1 : currentPageIndex, next.length-1)]?.blocks || [];
                                    setPost(p => ({ ...p, blocks: blk } as any));
                                    postStateRef.current = { ...postStateRef.current, blocks: blk } as any;
                                    return next;
                                  });
                                }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                              </button>
                              <button
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                title="Move down"
                                disabled={idx===pages.length-1}
                                onClick={() => {
                                  if (idx===pages.length-1) return;
                                  postRef.current?.commitPending?.();
                                  setPages(ps => {
                                    const next = ps.slice();
                                    const t = next[idx+1]; next[idx+1] = next[idx]; next[idx] = t;
                                    if (currentPageIndex === idx) setCurrentPageIndex(idx+1);
                                    else if (currentPageIndex === idx+1) setCurrentPageIndex(idx);
                                    const blk = next[Math.min(currentPageIndex === idx ? idx+1 : currentPageIndex, next.length-1)]?.blocks || [];
                                    setPost(p => ({ ...p, blocks: blk } as any));
                                    postStateRef.current = { ...postStateRef.current, blocks: blk } as any;
                                    return next;
                                  });
                                }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                              </button>
                              <button
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  postRef.current?.commitPending?.();
                                  setPages(ps => {
                                    const next = ps.filter((_,i)=>i!==idx);
                                    if (next.length === 0) {
                                      setRebuildingPage(true);
                                      setTimeout(() => {
                                        const template = [
                                          { id: Math.random().toString(36).slice(2, 10), type: 'heading1', text: 'Your amazing headline — Page 1' },
                                          { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Start writing your story here. You can make text bold, italic, add quotes, images and code blocks. Everything updates live.' },
                                          { id: Math.random().toString(36).slice(2, 10), type: 'quote', text: 'Simplicity is the soul of efficiency.' },
                                          { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Press the toolbar buttons to change styles or insert blocks. Click anywhere and type freely.' },
                                        ];
                                        const created = [{ title: 'Page 1', blocks: template }];
                                        setPages(created);
                                        setCurrentPageIndex(0);
                                        setPost(p => ({ ...p, blocks: template } as any));
                                        postStateRef.current = { ...postStateRef.current, blocks: template } as any;
                                        setRebuildingPage(false);
                                      }, 450);
                                      return [] as any;
                                    }
                                    let nextIndex = currentPageIndex;
                                    if (idx < currentPageIndex) nextIndex = Math.max(0, currentPageIndex - 1);
                                    else if (idx === currentPageIndex) nextIndex = Math.min(currentPageIndex, next.length - 1);
                                    setCurrentPageIndex(nextIndex);
                                    const blk = next[nextIndex]?.blocks || [];
                                    setPost(p => ({ ...p, blocks: blk } as any));
                                    postStateRef.current = { ...postStateRef.current, blocks: blk } as any;
                                    return next;
                                  });
                                }}
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <button
                          className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm hover:bg-gray-50"
                          onClick={() => {
                            const idx = pages.length;
                            const template = [
                              { id: Math.random().toString(36).slice(2, 10), type: 'heading1', text: `Your amazing headline — Page ${idx+1}` },
                              { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Start writing your story here. You can make text bold, italic, add quotes, images and code blocks. Everything updates live.' },
                              { id: Math.random().toString(36).slice(2, 10), type: 'quote', text: 'Simplicity is the soul of efficiency.' },
                              { id: Math.random().toString(36).slice(2, 10), type: 'paragraph', text: 'Press the toolbar buttons to change styles or insert blocks. Click anywhere and type freely.' },
                            ];
                            const newPage: PageModel = { title: `Page ${idx+1}`, blocks: template };
                            setPages(ps => [...ps, newPage]);
                            setCurrentPageIndex(idx);
                            setPost(p => ({ ...p, blocks: template } as any));
                            postStateRef.current = { ...postStateRef.current, blocks: template } as any;
                          }}
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                          Add Page
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          </aside>
          <button
            ref={toggleBtnRef}
            type="button"
            onClick={() => setLeftCollapsed(v => !v)}
            className={`fixed top-1/2 -translate-y-1/2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-purple-700 shadow-sm hover:bg-purple-50 transition ${leftCollapsed ? 'rotate-180' : ''}`}
            style={{ left: toggleLeftPx !== null ? `${toggleLeftPx}px` : undefined }}
            title={leftCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={leftCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        </div>

        <div className="relative h-full overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
          {(typeof (post as any).commentsEnabled === 'undefined') && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="relative inline-flex h-6 w-6">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-6 w-6 bg-gradient-to-br from-purple-600 to-blue-600 animate-spin [animation-duration:1200ms]" />
                </span>
                Preparing preview…
              </div>
            </div>
          )}
          <DisplayPost key={`${currentSlug || 'draft'}:${currentPageIndex}`} ref={postRef} post={post} editable onChange={handleChange} showInnerTitle={false} selectionArrowMode={selectionArrowMode} />
          {Boolean((post as any).commentsEnabled) && (
            <CommentSection slug={sidebarSettingsSlug || currentSlug || 'untitled'} />
          )}
          {pages.length > 1 && (
            <PageNavigation
              pages={pages as any}
              currentIndex={currentPageIndex}
              stylePreset={(post as any).navStyle || 'classic'}
              onSelect={(i) => {
                if (i === currentPageIndex) return;
                postRef.current?.commitPending?.();
                const currentBlocks = (postStateRef.current as any)?.blocks || post.blocks || [];
                setPages(ps => {
                  const arr = ps.slice();
                  if (arr[currentPageIndex]) arr[currentPageIndex] = { ...arr[currentPageIndex], blocks: currentBlocks } as any;
                  return arr;
                });
                const targetBlocks = pages[i]?.blocks || [];
                setCurrentPageIndex(i);
                setPost(p => ({ ...p, blocks: targetBlocks } as any));
                postStateRef.current = { ...postStateRef.current, blocks: targetBlocks } as any;
              }}
            />
          )}
        </div>
      </div>

      {selectingTitle && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <span className="relative inline-flex h-12 w-12">
              <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-12 w-12 bg-gradient-to-br from-purple-600 to-blue-600 animate-spin [animation-duration:1200ms]" />
            </span>
            <div className="text-sm font-medium text-gray-900">Opening…</div>
            <div className="text-xs text-gray-600 max-w-[60vw] text-center truncate">{selectingTitle}</div>
          </div>
        </div>
      )}

      {mediaOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center" data-editor-action>
          <div className="absolute inset-0 bg-black/40" onClick={closeMedia} />
          <div className="relative z-10 w-[min(920px,95vw)] max-h-[85vh] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
              <div className="font-semibold text-gray-900">Choose an image</div>
              <button className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100" onClick={()=>setMediaOpen(false)}>
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="px-4 pt-3 overflow-auto">
              <div className="mb-3 flex items-center gap-1 overflow-x-auto pr-2">
                {(['all', ...imageFilterKeys.filter(k=>k!=='all')] as string[]).map((key) => (
                  <button
                    key={key}
                    onClick={() => setMediaFilter(key)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap transition ${mediaFilter === key ? 'bg-purple-600 border-purple-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span className="capitalize">{key}</span>
                    <span className={`${mediaFilter === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'} ml-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1`}>{mediaCounts[key] ?? 0}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-4 pb-4 overflow-auto">
              {mediaLoading ? (
                <div className="text-sm text-gray-600">Loading media…</div>
              ) : mediaItems.length === 0 ? (
                <div className="text-sm text-gray-600">No images found. Open Media Gallery to upload.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {mediaItems
                    .filter(it => mediaFilter === 'all' ? true : imageExt(it) === mediaFilter)
                    .map(it => {
                    const isMulti = externalPickRef.current && externalAllowMultipleRef.current;
                    const selected = mediaMultiSel.includes(it.url);
                    const onClick = () => {
                      if (isMulti) {
                        setMediaMultiSel(prev => selected ? prev.filter(u => u !== it.url) : [...prev, it.url]);
                      } else {
                        onPickImage(it);
                      }
                    };
                    return (
                      <button key={it.name} className={`group relative aspect-square rounded-lg overflow-hidden border bg-gray-100 ${selected ? 'border-purple-400 ring-2 ring-purple-300' : 'border-gray-200'}`} onClick={onClick}>
                        <img src={it.url} alt={it.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                        {selected && (
                          <span className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-600 text-white shadow">
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-gray-100">
              <a href="/media" className="text-sm text-purple-700 hover:underline" target="_blank" rel="noreferrer">Open full Media Gallery</a>
              <div className="flex items-center gap-2">
                {externalPickRef.current && externalAllowMultipleRef.current && (
                  <>
                    <div className="text-sm text-gray-600">{mediaMultiSel.length} selected</div>
                    <button
                      className="inline-flex items-center justify-center rounded-md bg-purple-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                      disabled={mediaMultiSel.length === 0}
                      onClick={() => { try { const ev: any = new CustomEvent('media-picked', { detail: { urls: mediaMultiSel } }); window.dispatchEvent(ev); } catch {} closeMedia(); }}
                    >
                      Confirm
                    </button>
                  </>
                )}
                <button className="inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 px-3 py-1.5 text-sm" onClick={closeMedia}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && settingsModel && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={()=> !savingSettings && setSettingsOpen(false)} />
          <div className="relative z-10 w-[min(720px,95vw)] max-h-[85vh] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-semibold text-gray-900">Post Settings</div>
              <button className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100" onClick={()=> !savingSettings && setSettingsOpen(false)}>
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <PostSettings
              value={settingsModel}
              slug={settingsSlug || ''}
              onChange={(m, extras)=> {
                setSettingsModel(m as ApiPostModel);
                setSettingsSlug(extras.slug);
                // Mirror edits from popup into main editor state so sidebar reflects them
                const withBlocks = (m as any)?.blocks || post.blocks;
                setPost({ ...(post as any), ...(m as any), blocks: withBlocks } as DisplayPostModel);
                if (typeof (m as any).draft !== 'undefined') setIsDraft(!!(m as any).draft);
              }}
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50" onClick={()=> !savingSettings && setSettingsOpen(false)} disabled={savingSettings}>Cancel</button>
              <button
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white ${savingSettings ? 'bg-purple-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                onClick={async ()=>{
                  if (!settingsModel) return;
                  let slug = settingsSlug || ((settingsModel.title || '').toLowerCase().trim().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,''));
                  if (!slug) slug = 'untitled';
                  setSavingSettings(true);
                  try {
                    const payload: any = { ...settingsModel };
                    // carry over navStyle chosen in settings/sidebar or existing post
                    payload.navStyle = (settingsModel as any).navStyle ?? (post as any).navStyle;
                    // persist pages + current blocks for backward compatibility
                    payload.pages = pages.map(p => ({ title: p.title, blocks: p.blocks }));
                    payload.blocks = pages[currentPageIndex]?.blocks || [];
                    // infer cover across pages if missing
                    if (!payload.cover) {
                      try {
                        const firstImg = (pages.flatMap(p => p.blocks) || []).find((b: any) => b && b.type === 'image' && b.src);
                        if (firstImg && firstImg.src) payload.cover = firstImg.src;
                      } catch {}
                    }
                    await savePost(slug, payload);
                    if (settingsOrigSlug && settingsOrigSlug !== slug) {
                      try { await deletePost(settingsOrigSlug); } catch {}
                    }
                    setSettingsOpen(false);
                    if (currentSlug === settingsOrigSlug || currentSlug === slug) {
                      setCurrentSlug(slug);
                      const blocks = pages[currentPageIndex]?.blocks || (settingsModel as any)?.blocks || post.blocks || [];
                      setPost({ ...(post as any), ...(settingsModel as any), navStyle: payload.navStyle, blocks } as DisplayPostModel);
                    }
                    refreshPosts();
                  } finally { setSavingSettings(false); }
                }}
                disabled={savingSettings}
              >
                {savingSettings ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M22 12a10 10 0 0 1-10 10" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8"/><path d="M7 3v5h8"/></svg>
                )}
                <span>{savingSettings ? 'Saving…' : 'Save settings'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteSlug && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={()=> !deleting && setConfirmDeleteSlug(null)} />
          <div className="relative z-10 w-[min(520px,95vw)] bg-white rounded-2xl shadow-2xl border border-gray-200 p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-700 ring-1 ring-red-200">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Delete post?</div>
                <div className="mt-1 text-sm text-gray-600">This will permanently remove the post <span className="font-mono text-gray-800">{confirmDeleteSlug}</span>. This action cannot be undone.</div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50"
                onClick={()=> !deleting && setConfirmDeleteSlug(null)}
                disabled={deleting}
              >Cancel</button>
              <button
                onClick={async ()=>{
                  if (!confirmDeleteSlug) return;
                  setDeleting(true);
                  try {
                    await deletePost(confirmDeleteSlug);
                    if (currentSlug === confirmDeleteSlug) {
                      setCurrentSlug(null);
                    }
                    setConfirmDeleteSlug(null);
                    refreshPosts();
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white ${deleting ? 'bg-red-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {deleting ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M22 12a10 10 0 0 1-10 10" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                )}
                <span>{deleting ? 'Deleting…' : 'Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSlugModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={()=>!isSaving && setShowSlugModal(false)} />
          <div className="relative z-10 w-[min(560px,95vw)] bg-white rounded-2xl shadow-2xl border border-gray-200 p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Post name conflict</div>
                <div className="mt-1 text-sm text-gray-600">A post with this name already exists. You can choose a new unique name or keep the same name to overwrite the existing post.</div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <label className="block text-xs font-semibold text-gray-600 tracking-wide">Post slug</label>
              <input
                value={slugInput}
                onChange={(e)=>{ const v = slugify(e.target.value); setSlugInput(v); setSlugOverwrite(isSlugTaken(v)); }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="enter-a-unique-name"
              />
              {slugInput && !isSlugTaken(slugInput) && (
                <div className="text-xs text-green-600">This name is available.</div>
              )}
              {slugInput && isSlugTaken(slugInput) && (
                <div className="text-xs text-amber-700 flex items-center gap-2">
                  <span>This name exists. Saving will <span className="font-semibold">overwrite</span> the existing post.</span>
                  <button
                    className="inline-flex items-center rounded-md border border-amber-300 px-2 py-0.5 hover:bg-amber-50 text-amber-800"
                    onClick={() => { const s = suggestUniqueSlug(slugInput); setSlugInput(s); setSlugOverwrite(false); }}
                  >Use suggestion: {suggestUniqueSlug(slugInput)}</button>
                </div>
              )}
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50"
                onClick={()=> !isSaving && setShowSlugModal(false)}
                disabled={isSaving}
              >Cancel</button>
              <button
                onClick={()=> slugInput && performSaveWithSlug(slugInput)}
                disabled={!slugInput || isSaving}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white ${isSaving ? 'bg-gray-700' : slugOverwrite ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}
              >
                {isSaving ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M22 12a10 10 0 0 1-10 10" /></svg>
                ) : slugOverwrite ? (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M3 12h18"/><path d="M3 18h18"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8"/><path d="M7 3v5h8"/></svg>
                )}
                <span>{isSaving ? 'Saving…' : slugOverwrite ? 'Overwrite' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostEditor;
